"use client";

import { motion } from "motion/react";

const CORNER_MARK_CLASSES = [
  "left-6 top-6 border-l border-t rounded-tl-sm",
  "right-6 top-6 border-r border-t rounded-tr-sm",
  "left-6 bottom-6 border-l border-b rounded-bl-sm",
  "right-6 bottom-6 border-r border-b rounded-br-sm",
] as const;

/** Traveling highlight around the photo frame + soft shimmer (no full-screen wash). */
export const AnalyzeImageScanEffect = ({
  ringColor,
  glowColor,
  scanPersonaName,
  placeholderPins,
}: {
  ringColor: string;
  glowColor: string;
  scanPersonaName: string | null;
  placeholderPins: { top: number; left: number }[];
}) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-md">
    <style>{`
      @keyframes analyze-perimeter-run {
        to { stroke-dashoffset: -400; }
      }
      @keyframes analyze-grid-drift-coarse {
        0% {
          background-position: 0 0, 0 0, 0 0, 0 0;
        }
        100% {
          background-position: 40px 0, 0 40px, 40px 40px, 40px 40px;
        }
      }
      @keyframes analyze-grid-drift-fine {
        0% {
          background-position: 0 0, 0 0;
        }
        100% {
          background-position: -14px 14px, 14px -14px;
        }
      }
      @keyframes analyze-grid-pulse-lines {
        0%,
        100% {
          filter: brightness(1);
        }
        50% {
          filter: brightness(1.45);
        }
      }
    `}</style>

    <motion.div
      className="absolute inset-0 rounded-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0.38, 0.72, 0.38] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      style={{
        backgroundImage: `
          linear-gradient(to right, ${ringColor} 1.5px, transparent 1.5px),
          linear-gradient(to bottom, ${ringColor} 1.5px, transparent 1.5px),
          linear-gradient(to right, rgba(255, 255, 255, 0.22) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(255, 255, 255, 0.22) 1px, transparent 1px)
        `,
        backgroundSize: "40px 40px, 40px 40px, 40px 40px, 40px 40px",
        animation:
          "analyze-grid-drift-coarse 5s linear infinite, analyze-grid-pulse-lines 2.5s ease-in-out infinite",
      }}
    />

    <motion.div
      className="absolute inset-0 rounded-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0.22, 0.48, 0.22] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
      style={{
        backgroundImage: `
          linear-gradient(to right, ${glowColor} 1px, transparent 1px),
          linear-gradient(to bottom, ${glowColor} 1px, transparent 1px)
        `,
        backgroundSize: "10px 10px",
        animation: "analyze-grid-drift-fine 2.8s linear infinite",
      }}
    />

    <motion.div
      className="absolute inset-0 overflow-hidden rounded-md mix-blend-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0.15, 0.35, 0.15] }}
      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
    >
      <motion.div
        className="absolute h-full w-[30%]"
        style={{
          background: `linear-gradient(
            90deg,
            transparent,
            ${ringColor}55 45%,
            rgba(255, 255, 255, 0.35) 50%,
            ${ringColor}55 55%,
            transparent
          )`,
        }}
        initial={{ left: "-35%" }}
        animate={{ left: ["-35%", "105%"] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
      />
    </motion.div>

    <motion.div
      className="absolute inset-0 overflow-hidden rounded-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="absolute top-[-10%] bottom-[-10%] w-[45%]"
        style={{
          background: `linear-gradient(
            105deg,
            transparent 0%,
            ${ringColor}22 42%,
            ${ringColor}44 50%,
            ${ringColor}22 58%,
            transparent 100%
          )`,
        }}
        initial={{ left: "-50%" }}
        animate={{ left: ["-50%", "110%"] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.5 }}
      />
    </motion.div>

    <div
      className="absolute inset-4 rounded-lg"
      style={{ boxShadow: `inset 0 0 24px 2px ${glowColor}` }}
    />

    <svg
      className="absolute inset-5 h-[calc(100%-2.5rem)] w-[calc(100%-2.5rem)] overflow-visible"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
    >
      <rect
        x="1.5"
        y="1.5"
        width="97"
        height="97"
        rx="3"
        fill="none"
        stroke={ringColor}
        strokeWidth="0.75"
        opacity={0.18}
      />
      <rect
        x="1.5"
        y="1.5"
        width="97"
        height="97"
        rx="3"
        fill="none"
        stroke={ringColor}
        strokeWidth="0.75"
        strokeDasharray="5 395"
        opacity={0.42}
        style={{
          animation: "analyze-perimeter-run 6s linear infinite",
        }}
      />
    </svg>

    {CORNER_MARK_CLASSES.map((cls) => (
      <motion.span
        key={cls}
        className={`absolute h-3 w-3 ${cls}`}
        style={{ borderColor: ringColor, opacity: 0.28 }}
        animate={{ opacity: [0.18, 0.38, 0.18] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
      />
    ))}

    {placeholderPins.map((pin, i) => (
      <motion.span
        key={`ghost-${i}`}
        className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          top: `${pin.top}%`,
          left: `${pin.left}%`,
          backgroundColor: ringColor,
          boxShadow: `0 0 6px ${ringColor}`,
        }}
        animate={{ opacity: [0.15, 0.7, 0.15], scale: [1, 1.5, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.5 }}
      />
    ))}

    {scanPersonaName && (
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-white/15 bg-black/60 px-3 py-1 text-xs font-medium text-white shadow-md backdrop-blur-sm">
        <motion.span
          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full"
          style={{ backgroundColor: `${ringColor}33` }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ringColor }} />
        </motion.span>
        Analyzing · {scanPersonaName}
      </div>
    )}
  </div>
);
